import { apiRequest } from "./backendApi";

export type RunStatus =
  | "queued"
  | "running"
  | "cancelling"
  | "recovering_failure"
  | "recovering_cancellation"
  | "cancelled"
  | "completed"
  | "failed";

/** Matches server run list filters (`GET /api/runs` querystring). */
export type ListRunsFilters = {
  agent_id?: string;
  dataset_id?: string;
  base_dataset_id?: string;
  prompt_eval_only?: boolean;
  status?: RunStatus | "config_error";
  source?: "manual" | "scheduler" | "webhook";
  page?: number;
  limit?: number;
};

export type ListRunsResult = {
  runs: RunRow[];
  total: number;
};

export type RunRow = {
  id: string;
  agentId: string;
  datasetId: string;
  datasetLabel: string;
  baseDatasetId?: string | null;
  baseDatasetLabel?: string | null;
  comparisonId?: string | null;
  mode: "manual" | "scheduled";
  triggerSource: "manual" | "scheduler" | "webhook";
  status: RunStatus;
  startedAt: string;
  finishedAt?: string;
  totalItems: number;
  passedItems: number;
  failedItems: number;
  queuedItems?: number;
  processingItems?: number;
  skippedItems?: number;
};

export type RunItemRecord = {
  id: string;
  status: "passed" | "failed" | "running";
  summary: string;
  error?: string;
};

export type RunDetail = {
  runId: string;
  items: RunItemRecord[];
};

export type EvalCase = {
  id?: string;
  question: string;
  desired_answer: string;
  bank?: string;
  evaluation_basis?: "verified_reference" | "judge_only";
  reference_source?: string;
  reference_confidence?: number;
  metadata?: Record<string, unknown>;
};

export type LaunchRunInput = {
  agent_id: string;
  dataset_id: string;
  dataset_name?: string;
  comparison_id?: string;
  source: "manual";
  dry_run?: boolean;
  user_payload?: Record<string, unknown>;
  cases?: EvalCase[];
  prompts?: string[];
};

export type AppendCasesInput = {
  cases?: EvalCase[];
  prompts?: string[];
  comparison_id?: string;
};

export type DryRunCheck = {
  name: string;
  passed: boolean;
  message?: string;
};

export type DryRunResult = {
  dry_run: true;
  ready: boolean;
  checks: DryRunCheck[];
  estimated_duration_ms: number | null;
};

type RunsListResponse = {
  data: Array<{
    run_id: string;
    agent_id: string;
    source: "manual" | "scheduler" | "webhook";
    dataset_name?: string | null;
    dataset_id: string;
    base_dataset_id?: string | null;
    base_dataset_name?: string | null;
    comparison_id?: string | null;
    status: RunStatus | "config_error";
    counters: {
      total: number;
      queued?: number;
      processing?: number;
      completed: number;
      failed: number;
      skipped?: number;
    };
    started_at?: string | null;
    completed_at?: string | null;
    created_at: string;
  }>;
  count: number;
  total: number;
};

type RunItemsResponse = {
  data: Array<{
    item_id: string;
    item_index: number;
    status: "queued" | "processing" | "completed" | "failed" | "skipped";
    input?: unknown;
    expected_output?: unknown;
    output?: string | null;
    error?: string | null;
  }>;
  count: number;
  total: number;
};

export type RunPromptItem = {
  index: number;
  question: string;
  status: RunItemRecord["status"];
};

function extractQuestionFromInput(input: unknown): string {
  if (input && typeof input === "object" && "question" in input) {
    const question = (input as { question?: unknown }).question;
    if (typeof question === "string" && question.trim()) return question.trim();
  }
  if (typeof input === "string" && input.trim()) return input.trim();
  return "";
}

const toRunStatus = (status: RunStatus | "config_error"): RunStatus =>
  status === "config_error" ? "failed" : status;

const toMode = (source: "manual" | "scheduler" | "webhook"): "manual" | "scheduled" =>
  source === "scheduler" ? "scheduled" : "manual";

const toRunRow = (row: RunsListResponse["data"][number]): RunRow => ({
  id: row.run_id,
  agentId: row.agent_id,
  // Keep execution dataset separate from the logical/base dataset used for labels.
  datasetId: row.dataset_id,
  datasetLabel: row.dataset_name || row.dataset_id,
  baseDatasetId: row.base_dataset_id ?? null,
  baseDatasetLabel: row.base_dataset_name ?? null,
  comparisonId: row.comparison_id ?? null,
  mode: toMode(row.source),
  triggerSource: row.source,
  status: toRunStatus(row.status),
  startedAt: row.started_at || row.created_at,
  finishedAt: row.completed_at || undefined,
  totalItems: row.counters.total,
  passedItems: row.counters.completed,
  failedItems: row.counters.failed,
  queuedItems: row.counters.queued ?? 0,
  processingItems: row.counters.processing ?? 0,
  skippedItems: row.counters.skipped ?? 0,
});

const toItemStatus = (status: RunItemsResponse["data"][number]["status"]): RunItemRecord["status"] => {
  if (status === "completed") return "passed";
  if (status === "failed") return "failed";
  return "running";
};

export const runsApi = {
  async listRuns(filters?: ListRunsFilters): Promise<ListRunsResult> {
    const page = filters?.page ?? 1;
    const limit = filters?.limit ?? 100;
    const query: Record<string, string | number | boolean | undefined> = {
      page,
      limit,
      agent_id: filters?.agent_id || undefined,
      dataset_id: filters?.dataset_id || undefined,
      base_dataset_id: filters?.base_dataset_id || undefined,
      prompt_eval_only: filters?.prompt_eval_only ? "true" : undefined,
      status: filters?.status || undefined,
      source: filters?.source || undefined,
    };
    const res = await apiRequest<RunsListResponse>("GET", "/api/runs", { query });
    return {
      runs: res.data.map(toRunRow),
      total: res.total,
    };
  },
  async getRunDetail(runId: string): Promise<RunDetail | null> {
    const res = await apiRequest<RunItemsResponse>(
      "GET",
      `/api/runs/${encodeURIComponent(runId)}/items`,
      { query: { page: 1, limit: 100 } },
    );
    return {
      runId,
      items: res.data.map((item) => ({
        id: item.item_id,
        status: toItemStatus(item.status),
        summary:
          extractQuestionFromInput(item.input) ||
          item.output ||
          `Run item #${item.item_index + 1}`,
        error: item.error || undefined,
      })),
    };
  },
  async getRunPrompts(runId: string): Promise<RunPromptItem[]> {
    const res = await apiRequest<RunItemsResponse>(
      "GET",
      `/api/runs/${encodeURIComponent(runId)}/items`,
      { query: { page: 1, limit: 100 } },
    );
    return res.data
      .slice()
      .sort((a, b) => a.item_index - b.item_index)
      .map((item) => ({
        index: item.item_index,
        question:
          extractQuestionFromInput(item.input) ||
          `Case ${item.item_index + 1}`,
        status: toItemStatus(item.status),
      }));
  },
  async cancelRun(runId: string): Promise<RunRow> {
    const row = await apiRequest<RunsListResponse["data"][number]>(
      "POST",
      `/api/runs/${encodeURIComponent(runId)}/cancel`,
    );
    return toRunRow(row);
  },
  async deleteRun(runId: string): Promise<void> {
    await apiRequest<void>("DELETE", `/api/runs/${encodeURIComponent(runId)}`);
  },
  async launchRun(input: LaunchRunInput): Promise<RunRow> {
    const row = await apiRequest<RunsListResponse["data"][number]>("POST", "/api/runs", {
      body: { ...input, dry_run: false },
    });
    return toRunRow(row);
  },
  async dryRunLaunch(input: LaunchRunInput): Promise<DryRunResult> {
    return apiRequest<DryRunResult>("POST", "/api/runs", {
      body: { ...input, dry_run: true },
    });
  },
  async parsePrompts(
    prompts: string[],
    opts?: { datasetName?: string; datasetId?: string },
  ): Promise<{ cases: EvalCase[] }> {
    return apiRequest<{ cases: EvalCase[] }>("POST", "/api/runs/parse-cases", {
      body: {
        prompts,
        ...(opts?.datasetName ? { dataset_name: opts.datasetName } : {}),
        ...(opts?.datasetId ? { dataset_id: opts.datasetId } : {}),
      },
    });
  },
  async appendCases(runId: string, input: AppendCasesInput): Promise<RunRow> {
    const row = await apiRequest<RunsListResponse["data"][number]>(
      "POST",
      `/api/runs/${encodeURIComponent(runId)}/cases`,
      { body: input },
    );
    return toRunRow(row);
  },
  async getRun(runId: string): Promise<RunRow> {
    const row = await apiRequest<RunsListResponse["data"][number]>(
      "GET",
      `/api/runs/${encodeURIComponent(runId)}`,
    );
    return toRunRow(row);
  },
};
